import { useEffect, useRef } from 'react';

/**
 * Detects when a new frontend build has been deployed and fires `onUpdate()`.
 *
 * How it works:
 *   1. At mount we capture the hashed filename of the currently-loaded main
 *      JS bundle (e.g. `main.a3f9b21c.js`). This is stable for the lifetime
 *      of the tab.
 *   2. Every `pollMs` (default 5 min) we re-fetch `/index.html` with
 *      `cache: 'no-store'` so we bypass any HTTP cache, parse the *new*
 *      main-bundle filename out of it, and compare.
 *   3. If it differs, the user is running old code — we call `onUpdate()`
 *      which typically shows a toast prompting them to refresh.
 *
 * Design choices:
 *   - We deliberately do NOT use a service worker. Introducing one adds
 *     another caching layer with its own lifecycle (installing → waiting →
 *     active) — the exact class of bug we just spent a session fixing.
 *     Polling `/index.html` costs a single ~2 KB request every 5 min and
 *     has zero cache-invalidation risk.
 *   - We pause polling while the tab is hidden to avoid burning battery /
 *     data on inactive tabs. When the user returns we check immediately.
 *   - Failures (offline, 5xx, malformed HTML) are swallowed silently —
 *     we just try again next tick. A missing bundle name doesn't
 *     mis-trigger the toast.
 */

const parseMainBundleName = (html) => {
  // CRA emits <script defer="defer" src="/static/js/main.<hash>.js"></script>
  const match = html.match(/\/static\/js\/main\.[A-Za-z0-9]+\.js/);
  return match ? match[0] : null;
};

const getCurrentBundleName = () => {
  const scripts = Array.from(document.getElementsByTagName('script'));
  for (const s of scripts) {
    const src = s.getAttribute('src') || '';
    const m = src.match(/\/static\/js\/main\.[A-Za-z0-9]+\.js/);
    if (m) return m[0];
  }
  return null;
};

const useDeployWatcher = (onUpdate, { pollMs = 5 * 60 * 1000 } = {}) => {
  const currentRef = useRef(null);

  useEffect(() => {
    currentRef.current = getCurrentBundleName();
    // If we can't identify the current bundle (e.g. dev mode where CRA
    // doesn't hash the filename) there's nothing meaningful to poll for.
    if (!currentRef.current) return undefined;

    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await fetch(`/?_v=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const html = await res.text();
        const next = parseMainBundleName(html);
        if (next && next !== currentRef.current) {
          // Advance the tracked hash so the SAME deploy transition doesn't
          // re-fire on every subsequent poll (which would re-open the banner
          // if the user dismissed it). A LATER deploy (a third hash) will
          // still trigger — important for long-lived tabs during multiple
          // deploy windows.
          currentRef.current = next;
          onUpdate();
        }
      } catch {
        // Silent — try again next tick.
      }
    };

    const interval = setInterval(check, pollMs);
    // Check immediately whenever the tab regains focus so a returning user
    // isn't stuck on a stale build for another 5 minutes.
    const onVisibility = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [onUpdate, pollMs]);
};

export default useDeployWatcher;
